import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { Mail, Plus, RefreshCw, Send, Upload } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CardEditor } from "@/components/newsletter/CardEditor";
import { LintPanel } from "@/components/newsletter/LintPanel";
import { LivePreview } from "@/components/newsletter/LivePreview";
import { useBlastMailboxes } from "@/hooks/useBlast";
import {
  useDealPicker, useNewsletterLists, useNewsletterIssue, useCreateIssue, useSetProjectName,
  useUpdateIssue, useRefreshIssue, useLintIssue, useBuildRecipients,
} from "@/hooks/useNewsletter";
import type { NewsletterCard, LintFinding } from "@shared/newsletterTypes";
import { describeSendWindow, isInSendWindow, type SendWindow } from "@shared/sendWindow";

/**
 * A launched campaign row, in the shape the send-window helpers want. The
 * composer exposes no send-window control (deliberately, that is a blast-level
 * setting), so a newsletter always inherits 8am to 6pm, Monday to Friday,
 * Central. Launching outside that queues silently, which is exactly the kind
 * of nothing-happened the user must be told about.
 */
const sendWindowOf = (campaign: any): SendWindow => ({
  sendWindowStart: Number(campaign?.send_window_start ?? 8),
  sendWindowEnd: Number(campaign?.send_window_end ?? 18),
  sendDays: Array.isArray(campaign?.send_days) ? campaign.send_days.map(Number) : null,
  timezone: typeof campaign?.timezone === "string" ? campaign.timezone : null,
});

export default function NewsletterComposer() {
  const { id } = useParams<{ id?: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const { data: deals = [] } = useDealPicker();
  const { data: lists = [] } = useNewsletterLists();
  const { data: mailboxes = [] } = useBlastMailboxes();
  const { data: issue } = useNewsletterIssue(id);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [cards, setCards] = useState<NewsletterCard[]>([]);
  // null = a deal-card issue. A string (even empty) = a custom-HTML issue,
  // whose letter is the whole body. Chosen once, before the first save.
  const [customHtml, setCustomHtml] = useState<string | null>(null);
  const [listIds, setListIds] = useState<string[]>([]);
  // Mailboxes this issue rotates across. Empty = every active mailbox, the
  // same default a teaser campaign has (newsletters rotate since 2026-08-20).
  const [mailboxIds, setMailboxIds] = useState<string[]>([]);
  const [findings, setFindings] = useState<LintFinding[]>([]);
  const [checked, setChecked] = useState(false);
  const [testing, setTesting] = useState(false);
  // Bumped by every edit that touches cards or intro. save()/runRefresh()
  // capture the value before their await and only apply the response if it
  // hasn't moved, so a response for an older edit can never clobber a newer
  // one still sitting in local state (see save()/runRefresh() below).
  const editGen = useRef(0);

  // Load an existing draft once. The issue is frozen server-side on first
  // edit, so this never fights the user's typing with fresh CRM data. This
  // is deliberately NOT how a save/refresh syncs local state back afterward
  // (issue?.id doesn't change on those round trips) — see save()/runRefresh().
  useEffect(() => {
    if (!issue) return;
    setName(issue.name);
    setSubject(issue.subject);
    setIntro(issue.intro_text ?? "");
    setCards(issue.cards);
    setCustomHtml(issue.custom_html ?? null);
    setListIds(issue.list_ids ?? []);
    setMailboxIds(issue.mailbox_ids ?? []);
  }, [issue?.id]);

  const create = useCreateIssue();
  const setProjectName = useSetProjectName();
  const update = useUpdateIssue(id ?? "");
  const refresh = useRefreshIssue(id ?? "");
  const lint = useLintIssue(id ?? "");
  const buildRecipients = useBuildRecipients(id ?? "");

  const selectedIds = useMemo(() => new Set(cards.map((c) => c.clientId)), [cards]);
  const reachable = lists.filter((l) => listIds.includes(l.id)).reduce((s, l) => s + l.contactCount, 0);

  // A card change invalidates any prior Check: findings and the green/red
  // state they drove described the PREVIOUS content, and showing that as
  // still-current would tell the user the opposite of the truth right when
  // it matters most. Cleared here rather than left stale until the next
  // Check actually runs.
  const invalidateCheck = () => {
    editGen.current += 1;
    setFindings([]);
    setChecked(false);
  };

  const toggleDeal = (dealId: string) => {
    const deal = deals.find((d) => d.id === dealId);
    if (!deal) return;
    invalidateCheck();
    setCards((prev) =>
      prev.some((c) => c.clientId === dealId)
        ? prev.filter((c) => c.clientId !== dealId).map((c, i) => ({ ...c, sortOrder: i }))
        : [...prev, { ...deal.preview, sortOrder: prev.length }],
    );
  };

  const moveCard = (index: number, direction: -1 | 1) => {
    invalidateCheck();
    setCards((prev) => {
      const next = [...prev].sort((a, b) => a.sortOrder - b.sortOrder);
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((c, i) => ({ ...c, sortOrder: i }));
    });
  };

  // Returns whether the save actually landed, so callers (runLint, launch)
  // know whether it's safe to keep going. Every branch that succeeds
  // re-syncs local state from what the mutation actually returned (not just
  // what was sent), since the server rewrites card/intro prose at store time
  // (stripCardProse) and the response is genuinely different from what was
  // sent. That sync is guarded by editGen: it's captured before the await,
  // and only applied if nothing has edited cards/intro in the meantime — an
  // edit made while this request was in flight bumps editGen and must win
  // over a response describing the version that predates it.
  const save = async (): Promise<boolean> => {
    if (!name.trim() || !subject.trim()) {
      toast({ title: "Name and subject are required", variant: "destructive" });
      return false;
    }
    const gen = editGen.current;
    try {
      if (customHtml !== null) {
        // A custom-HTML issue sends its letter exactly as written, so the
        // response carries nothing new to sync back beyond what was sent.
        if (id) {
          await update.mutateAsync({ name, subject, customHtml, listIds, mailboxIds });
          toast({ title: "Saved" });
        } else {
          const created = await create.mutateAsync({
            name, subject, intro: "", listIds, clientIds: [], customHtml,
            mailboxIds,
          });
          navigate(`/campaigns/newsletter/${created.id}/edit`);
        }
        return true;
      }
      if (id) {
        const updated = await update.mutateAsync({ name, subject, intro, cards, listIds, mailboxIds });
        if (editGen.current === gen) {
          setCards(updated.cards);
          setIntro(updated.intro_text ?? "");
        }
        toast({ title: "Saved" });
      } else {
        const created = await create.mutateAsync({
          name, subject, intro, listIds,
          clientIds: cards.map((c) => c.clientId),
          // Sends whatever the user already typed into a card before this
          // first save, so it survives rather than being replaced by fresh
          // auto-fill. The server still decides which cards exist from
          // clientIds; this only overlays prose onto ones that exist.
          cards,
          mailboxIds,
        });
        if (editGen.current === gen) {
          setCards(created.cards);
          setIntro(created.intro_text ?? "");
        }
        navigate(`/campaigns/newsletter/${created.id}/edit`);
      }
      return true;
    } catch (e) {
      toast({
        title: id ? "Save failed" : "Could not create issue",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const runLint = async () => {
    // Same staleness guard as save()/runRefresh(): if the user edits a card
    // while this Check is in flight, invalidateCheck() already cleared
    // findings/checked for the new content, and this call's result — which
    // describes the version that existed when it was sent — must not
    // overwrite that with a stale green (or red).
    const gen = editGen.current;
    if (!(await save())) return;
    try {
      const res = await lint.mutateAsync();
      if (editGen.current === gen) {
        setFindings(res.findings);
        setChecked(true);
      }
    } catch (e) {
      toast({
        title: "Check failed",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  // The only preview an issue has: NewsletterComposer renders no email
  // preview of its own, so this is how anyone sees the thing before it goes
  // to the whole buyer network. Saves first (same reason runLint does) so the
  // preview shows what is on screen rather than what was last persisted. The
  // server signs every link with the caller's own address, so clicking one in
  // the test email records the tester and never touches a buyer.
  const sendTest = async () => {
    if (!(await save())) return;
    setTesting(true);
    try {
      const res = await fetch(`/api/blast/campaigns/${id}/test-send`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({ title: "Test send failed", description: body.error ?? "Please try again.", variant: "destructive" });
        return;
      }
      toast({ title: `Test sent to ${body.sentTo}`, description: "Every link in it points at you, not at a buyer." });
    } catch (e) {
      toast({
        title: "Test send failed",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  // Refreshing overwrites the server's cards with a fresh CRM pull, which
  // is destructive to any edit sitting in this screen. Confirm first, then
  // apply the mutation's own response so the cards visibly change: waiting
  // on the query invalidation alone does nothing, because the load effect
  // above only re-runs when issue?.id changes, and a refresh keeps the same id.
  const runRefresh = async () => {
    if (!window.confirm("Refresh from CRM discards your edits. Continue?")) return;
    const gen = editGen.current;
    try {
      const updated = await refresh.mutateAsync();
      if (editGen.current === gen) {
        // Refresh is the path that most needs this: it re-pulls straight
        // from companies.description, which is exactly the field that
        // tends to carry the client's own name. Apply the fresh content
        // first, then invalidate — invalidating first would bump editGen
        // before the comparison above runs against it, racing its own gen
        // check. Bumping after also means a save()/runLint() that was
        // already in flight before this refresh started will correctly
        // lose to this newer content when it lands.
        setCards(updated.cards);
        setIntro(updated.intro_text ?? "");
        invalidateCheck();
      }
    } catch (e) {
      toast({
        title: "Refresh failed",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const launch = async () => {
    // Recipients and the launch endpoint both act on server state. Saving
    // first (same pattern as runLint) guarantees the send matches what's on
    // screen instead of whatever was last persisted.
    if (!(await save())) return;

    let res: Awaited<ReturnType<typeof buildRecipients.mutateAsync>>;
    try {
      res = await buildRecipients.mutateAsync();
    } catch (e) {
      toast({
        title: "Could not build recipients",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
      return;
    }
    // A list deleted or archived since this issue referenced it resolves to
    // nothing, and that's the more useful thing to tell the user: it's also
    // why added can be 0, so it must be checked first, or a deleted list
    // shows the generic "suppressed or empty" message instead of the
    // actionable one. Without catching this at all, the issue would send to
    // a fraction of its intended audience and report success, which is the
    // failure nobody notices.
    if (res.missingLists.length > 0) {
      toast({
        title: `${res.missingLists.length} list${res.missingLists.length > 1 ? "s" : ""} no longer exist`,
        description: "This issue references a list that was deleted or archived. Fix the audience before sending.",
        variant: "destructive",
      });
      return;
    }
    if (res.added === 0) {
      toast({ title: "No recipients", description: "Every match is suppressed or the lists are empty.", variant: "destructive" });
      return;
    }
    try {
      const launchRes = await fetch(`/api/blast/campaigns/${id}/launch`, { method: "POST", credentials: "include" });
      if (!launchRes.ok) {
        const body = await launchRes.json().catch(() => ({}));
        if (body.findings) {
          setFindings(body.findings);
          setChecked(true);
        }
        toast({ title: "Cannot send", description: body.error ?? "Launch failed", variant: "destructive" });
        return;
      }
      // Launch only sets status; the tick sends, and only inside the
      // campaign's send window. Launching at 7pm on a Friday therefore does
      // nothing until Monday 8am, and "Sending to N contacts" would be a
      // straight lie for the whole weekend. Computed from the campaign's own
      // values (the launch response is the campaign row) rather than from the
      // 8-to-6 default, so a campaign with different rules says so.
      const launched = await launchRes.json().catch(() => null);
      // Not named `window`: that shadows the global this file also uses.
      const sendWindow = sendWindowOf(launched);
      if (!isInSendWindow(sendWindow)) {
        toast({
          title: `Queued for ${res.added} contacts`,
          description: `Sending starts at the next send window: ${describeSendWindow(sendWindow)}.`,
        });
      } else {
        toast({ title: `Sending to ${res.added} contacts` });
      }
      navigate("/campaigns/newsletter");
    } catch (e) {
      toast({
        title: "Cannot send",
        description: e instanceof Error ? e.message : "Launch failed",
        variant: "destructive",
      });
    }
  };

  const sorted = [...cards].sort((a, b) => a.sortOrder - b.sortOrder);
  const hasErrors = findings.some((f) => f.level === "error");
  // Send requires a CURRENT check, not just an absence of errors: checked
  // is cleared by invalidateCheck() on every card/intro edit, so an edit
  // made after a green Check immediately makes Send unavailable again until
  // the user re-checks, rather than sending on a check that describes a
  // version of the issue that no longer exists.
  const sendReady = checked && !hasErrors;

  // A frozen issue keeps its cards even when an engagement closes underneath
  // it. Warn rather than mutate: the cards are a published snapshot.
  const stale = cards.filter((c) => !deals.some((d) => d.id === c.clientId));

  const loadHtmlFile = async (file: File | undefined) => {
    if (!file) return;
    setCustomHtml(await file.text());
    invalidateCheck();
  };

  return (
    <AppLayout title={id ? "Edit issue" : "New issue"}>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_460px] gap-6 page-enter">
      <div className="space-y-5 min-w-0">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="issue-name">Issue name (internal)</Label>
            <Input id="issue-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="August 2026 pipeline" />
          </div>
          <div>
            <Label htmlFor="issue-subject">Subject line</Label>
            <Input id="issue-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Current opportunities, August" />
          </div>
        </div>

        {!id && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Build this issue from</span>
            <Button size="sm" variant={customHtml === null ? "default" : "outline"}
              onClick={() => { setCustomHtml(null); invalidateCheck(); }}>
              Deal cards
            </Button>
            <Button size="sm" variant={customHtml !== null ? "default" : "outline"}
              onClick={() => { setCustomHtml((prev) => prev ?? ""); invalidateCheck(); }}>
              Custom HTML
            </Button>
          </div>
        )}

        {customHtml !== null ? (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="issue-html">Letter HTML</Label>
              <label className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary-text cursor-pointer">
                <Upload className="w-3 h-3" /> Load an .html file
                <input type="file" accept=".html,.htm,text/html" className="sr-only"
                  onChange={(e) => { void loadHtmlFile(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
            <Textarea id="issue-html" rows={18} className="font-mono text-xs" value={customHtml}
              onChange={(e) => { setCustomHtml(e.target.value); invalidateCheck(); }}
              placeholder="Paste the full email body. Use cid:ftc-logo for the logo and ?token=__UNSUB_TOKEN__ on the unsubscribe link." />
            <p className="text-xs text-muted-foreground">
              Sent exactly as written, with no mailbox signature. The compliance footer is added at send time.
              Check runs the anonymity lint against every client in the CRM.
            </p>
          </section>
        ) : (<>
        <div>
          <Label htmlFor="issue-intro">Intro</Label>
          <Textarea id="issue-intro" rows={3} value={intro} onChange={(e) => { setIntro(e.target.value); invalidateCheck(); }}
            placeholder="A line or two. Blank lines start a new paragraph." />
        </div>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold">Deals in this issue</h2>
          <div className="flex flex-wrap gap-2">
            {deals.map((d) => (
              <Button
                key={d.id}
                variant={selectedIds.has(d.id) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleDeal(d.id)}
                className="gap-1"
              >
                {!selectedIds.has(d.id) && <Plus className="w-3 h-3" />}
                {d.name}
              </Button>
            ))}
            {deals.length === 0 && (
              <p className="text-sm text-muted-foreground">No active client engagements to feature.</p>
            )}
          </div>
        </section>

        <section className="space-y-3">
          {sorted.map((card, i) => (
            <CardEditor
              key={card.clientId}
              card={card}
              index={i}
              findings={findings}
              onChange={(next) => { setCards((prev) => prev.map((c) => (c.clientId === next.clientId ? next : c))); invalidateCheck(); }}
              onRemove={() => toggleDeal(card.clientId)}
              onMove={(dir) => moveCard(i, dir)}
            />
          ))}
        </section>
        </>)}

        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">Who receives it</h2>
            <Link href="/campaigns/newsletter/lists" className="text-xs text-muted-foreground hover:text-primary-text hover:underline">
              Manage lists
            </Link>
          </div>
          {lists.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={listIds.includes(l.id)}
                onCheckedChange={(v) =>
                  setListIds((prev) => (v ? [...prev, l.id] : prev.filter((x) => x !== l.id)))
                }
              />
              <span>{l.name}</span>
              <span className="text-muted-foreground">{l.contactCount} contacts at {l.buyerCount} buyers</span>
            </label>
          ))}
          {listIds.length > 1 && (
            <p className="text-xs text-muted-foreground">
              A buyer on two lists receives one copy. Reach shown before de-duplication: {reachable}.
            </p>
          )}
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold">Send from</h2>
          {mailboxes.filter((m) => m.isActive && m.hasToken).map((m) => (
            <label key={m.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={mailboxIds.includes(m.id)}
                onCheckedChange={(v) =>
                  setMailboxIds((prev) => (v ? [...prev, m.id] : prev.filter((x) => x !== m.id)))
                }
              />
              <span>{m.fromAddress}</span>
              {m.displayName && <span className="text-muted-foreground">{m.displayName}</span>}
            </label>
          ))}
          <p className="text-xs text-muted-foreground">
            {mailboxIds.length === 0
              ? "None checked: the issue rotates across every active mailbox."
              : mailboxIds.length === 1
                ? "One checked: every copy comes from this mailbox."
                : `Rotates across these ${mailboxIds.length} mailboxes.`}
            {" "}Each mailbox's daily cap and warmup still apply, so a large list can take more than a day.
          </p>
        </section>

        {customHtml === null && stale.length > 0 && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            {stale.length === 1 ? "One featured deal is" : `${stale.length} featured deals are`} no longer an active engagement. Remove {stale.length === 1 ? "it" : "them"} if the deal should not go out.
          </p>
        )}

        <LintPanel findings={findings} checked={checked} />

        <div className="flex items-center gap-2 border-t pt-4">
          <Button variant="outline" onClick={save}>Save draft</Button>
          {id && customHtml === null && (
            <Button variant="outline" onClick={runRefresh} className="gap-1">
              <RefreshCw className="w-4 h-4" /> Refresh from CRM
            </Button>
          )}
          {id && <Button variant="outline" onClick={runLint}>Check</Button>}
          {id && (
            <Button variant="outline" onClick={sendTest} disabled={testing} className="gap-1">
              <Mail className="w-4 h-4" /> {testing ? "Sending test..." : "Send test to myself"}
            </Button>
          )}
          {id && (
            <Button onClick={launch} disabled={!sendReady || listIds.length === 0} className="gap-1">
              <Send className="w-4 h-4" /> Send issue
            </Button>
          )}
        </div>
        {id && !sendReady && (
          <p className={hasErrors ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
            {hasErrors ? "Fix the errors above before sending." : "Check the issue before you can send it."}
          </p>
        )}
      </div>

      {/* Same renderer the send uses, so what is on screen cannot disagree
          with what ships. Sticky: a long card list should not scroll the
          preview out of view while you are editing against it. */}
      <div className="xl:sticky xl:top-4 xl:self-start">
        <LivePreview intro={intro} cards={cards} customHtml={customHtml} />
      </div>
      </div>
    </AppLayout>
  );
}
